import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ShoppingBag, Leaf, Sparkles, Gift, ArrowRight, BadgeCheck } from "lucide-react";
import api from "../lib/api";
import Nav from "../components/Nav";
import Footer from "../components/Footer";

const CATEGORY_META = {
  wellness: { icon: Leaf, title: "Wellness", title_hi: "वेलनेस", desc: "Daily rituals for well-being.", desc_hi: "स्वास्थ्य के लिए दैनिक अनुष्ठान।" },
  aura: { icon: Sparkles, title: "Aura", title_hi: "ऑरा", desc: "Presence, confidence, identity.", desc_hi: "उपस्थिति, आत्मविश्वास, पहचान।" },
  all: { icon: ShoppingBag, title: "All", title_hi: "सब", desc: "The full collection.", desc_hi: "पूरा संग्रह।" },
};

export default function Shop() {
  const { category = "all" } = useParams();
  const { i18n } = useTranslation();
  const isHi = i18n.language?.startsWith("hi");
  const [products, setProducts] = useState([]);
  const meta = CATEGORY_META[category] || CATEGORY_META.all;
  const Icon = meta.icon;

  useEffect(() => {
    (async () => {
      try {
        const q = category !== "all" ? `?category=${category}` : "";
        const r = await api.get(`/products${q}`);
        setProducts(r.data);
      } catch (e) { /* */ }
    })();
  }, [category]);

  return (
    <div className="min-h-screen bg-background">
      <Nav />
      <section className="brand-gradient-bg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-14 pb-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary text-primary-foreground grid place-items-center">
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <div className="label-eyebrow">VyomSutra</div>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
                {isHi ? meta.title_hi : meta.title}
              </h1>
            </div>
          </div>
          <p className="mt-4 text-muted-foreground max-w-xl">{isHi ? meta.desc_hi : meta.desc}</p>

          {/* Category tabs */}
          <div className="mt-6 inline-flex gap-1 rounded-md border border-border bg-white p-1">
            {["all", "wellness", "aura"].map((c) => (
              <Link key={c} to={`/shop/${c}`}
                className={`px-3 py-1.5 rounded text-xs font-semibold uppercase tracking-widest ${
                  category === c ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                }`}
                data-testid={`shop-cat-${c}`}
              >
                {c === "all" ? (isHi ? "सब" : "All") : c === "wellness" ? (isHi ? "वेलनेस" : "Wellness") : (isHi ? "ऑरा" : "Aura")}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {products.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">Loading…</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Link to={`/products/${p.slug}`} className="group block" data-testid={`product-card-${p.slug}`}>
                    <div className="relative aspect-square rounded-xl overflow-hidden border border-border bg-white">
                      <img src={p.image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      {p.badge && (
                        <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded bg-accent text-accent-foreground">
                          {p.badge}
                        </span>
                      )}
                    </div>
                    <div className="mt-4">
                      <div className="text-xs uppercase tracking-widest text-muted-foreground">{p.collection || p.category}</div>
                      <div className="mt-1 font-display font-bold text-lg leading-tight">
                        {isHi && p.title_hi ? p.title_hi : p.title}
                      </div>
                      <div className="mt-2 flex items-baseline gap-2">
                        <span className="font-display text-xl font-extrabold text-primary">₹{p.price_inr.toLocaleString("en-IN")}</span>
                        {p.compare_at_inr && (
                          <span className="text-xs text-muted-foreground line-through">₹{p.compare_at_inr.toLocaleString("en-IN")}</span>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          {/* Coupon nudge */}
          <div className="mt-14 rounded-2xl border border-accent/40 bg-accent/8 p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-5">
            <div className="w-11 h-11 rounded-md bg-accent text-accent-foreground grid place-items-center flex-shrink-0">
              <Gift className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="font-display text-xl font-extrabold">
                {isHi ? "50% छूट कूपन कमाएँ" : "Earn a 50% OFF coupon"}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {isHi
                  ? "प्रत्येक भुगतान किए गए BharatIQ मूल्यांकन के साथ अपने पहले VyomSutra उत्पाद पर 50% छूट पाएँ। 30 दिनों के लिए मान्य।"
                  : "Complete any paid BharatIQ assessment to unlock 50% OFF on your first VyomSutra product. Valid for 30 days."}
              </p>
            </div>
            <Link to="/bharatiq" className="btn-primary inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold flex-shrink-0" data-testid="shop-earn-coupon">
              {isHi ? "मूल्यांकन शुरू करें" : "Start assessment"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
